import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import RoomsSection from '../components/RoomsSection'
import PublicSection from '../components/PublicSection'
import CreateRoomModal from '../components/CreateRoomModal'
import PlayPublicModal from '../components/PlayPublicModal'
import { useTranslation } from '../hooks/use-i18n'
import { getMyPublicEnrollments, QUERY_KEY_MY_PUBLIC_ENROLLMENTS } from '../services/publicService'
import { isAdminUser } from '../utils/admin'
import {
  asPublicEnrollmentList,
  findActiveFootballParticipant,
  findActiveFormula1Participant,
  findPublicLocationFromEnrollments,
} from '../utils/publicFootballEnrollment'
import { syncWorldEnrollCache, worldHubPath, type WorldEnrollExtras } from '../utils/worldEnrollCache'
// import { HIDE_IN_APP_CHESS } from '../constants/chessProduct' // CHESS_HIDDEN flag available

const btnGreen =
  'inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg sm:rounded-xl bg-green-800 text-white text-small sm:text-nav font-semibold hover:bg-green-700 transition-all w-full sm:w-auto'

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [showCreateModal, setShowCreateModal] = useState(false)
  // CHESS_HIDDEN: in-app chess arena create — play on chessarenas.com
  // const [showCreateChessArenaModal, setShowCreateChessArenaModal] = useState(false)
  const [showPlayPublicModal, setShowPlayPublicModal] = useState(false)
  const isAdmin = isAdminUser(user)

  const { data: enrollmentsRaw } = useQuery({
    queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
    queryFn: getMyPublicEnrollments,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })
  const enrollments = asPublicEnrollmentList(enrollmentsRaw)
  const hasFootballEnrollment = Boolean(findActiveFootballParticipant(enrollments))
  const hasFormula1Enrollment = Boolean(findActiveFormula1Participant(enrollments))
  const hasAnyPlayWorldEnrollment = hasFootballEnrollment || hasFormula1Enrollment
  const existingLocation = findPublicLocationFromEnrollments(enrollments)

  const handleWorldEnrolled = async (
    _tournamentId: string,
    gameType?: string,
    extras?: WorldEnrollExtras,
  ) => {
    setShowPlayPublicModal(false)
    await syncWorldEnrollCache(queryClient, gameType, extras)
    navigate(worldHubPath(gameType))
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6">
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="font-display text-xl sm:text-display-2 lg:text-display-1 font-bold mb-1.5 sm:mb-2 text-foreground">{t('dashboard.title')}</h1>
            <p className="text-small sm:text-body-lg text-muted-foreground">{t('dashboard.subtitle')}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto justify-center sm:justify-start mb-6 sm:mb-8">
            <button onClick={() => setShowCreateModal(true)} className={btnGreen}>
              {t('dashboard.createRoom')}
            </button>
            {/* CHESS_HIDDEN: create chess arena in BRC — use chessarenas.com
            <button onClick={() => setShowCreateChessArenaModal(true)} className={btnGreen}>
              ♟️ {t('dashboard.createChessArena')}
            </button>
            */}
            {isAdmin && (
              <Link to="/admin/update-results" className={btnGreen}>
                ⚽ Update match results 🏎️
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin/social-posters" className={btnGreen}>
                📣 Social posters
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin/missing-predictions" className={btnGreen}>
                ⚠️ {t('adminMissing.title')}
              </Link>
            )}
            {!hasAnyPlayWorldEnrollment && (
              <button onClick={() => setShowPlayPublicModal(true)} className={btnGreen}>
                <span className="inline-block text-[1.6em] leading-none">⚽</span>{' '}
                {t('dashboard.playWorld')}{' '}
                <span className="inline-block text-[1.6em] leading-none">🏎️</span>
              </button>
            )}
          </div>

          <PublicSection onRequestPlayWorld={() => setShowPlayPublicModal(true)} onWorldEnrolled={handleWorldEnrolled} />
          <RoomsSection
            onOpenCreateRoom={() => setShowCreateModal(true)}
            // CHESS_HIDDEN: onOpenCreateChessArena={() => setShowCreateChessArenaModal(true)}
            onWorldEnrolled={(gameType, extras) => handleWorldEnrolled('', gameType, extras)}
          />

          {showCreateModal && (
            <CreateRoomModal
              onClose={() => setShowCreateModal(false)}
              onSuccess={() => {
                setShowCreateModal(false)
                queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
              }}
            />
          )}
          {/* CHESS_HIDDEN: create chess arena modal
          {showCreateChessArenaModal && (
            <CreateRoomModal
              chessArenaOnly
              onClose={() => setShowCreateChessArenaModal(false)}
              onSuccess={() => {
                setShowCreateChessArenaModal(false)
                queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
              }}
            />
          )}
          */}
          {showPlayPublicModal && (
            <PlayPublicModal
              onClose={() => setShowPlayPublicModal(false)}
              onSuccess={handleWorldEnrolled}
              initialCountryCode={existingLocation?.countryCode}
              initialCity={existingLocation?.city}
              onlyGameType={
                hasFootballEnrollment && !hasFormula1Enrollment
                  ? 'formula1'
                  : !hasFootballEnrollment && hasFormula1Enrollment
                    ? 'football'
                    : undefined
              }
            />
          )}

          <div className="mt-6 sm:mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:bg-muted/10 transition-all">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <h3 className="text-small sm:text-body-lg font-semibold text-foreground">{t('dashboard.totalRooms')}</h3>
                <span className="text-xl sm:text-2xl">🏠</span>
              </div>
              <p className="text-lg sm:text-display-2 font-bold text-primary mb-1 sm:mb-2">0</p>
              <p className="text-small text-muted-foreground">{t('dashboard.totalRoomsDesc')}</p>
            </div>
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:bg-muted/10 transition-all">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <h3 className="text-small sm:text-body-lg font-semibold text-foreground">{t('dashboard.predictionsMade')}</h3>
                <span className="text-xl sm:text-2xl">🎯</span>
              </div>
              <p className="text-lg sm:text-display-2 font-bold text-primary mb-1 sm:mb-2">0</p>
              <p className="text-small text-muted-foreground">{t('dashboard.predictionsMadeDesc')}</p>
            </div>
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:bg-muted/10 transition-all sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <h3 className="text-small sm:text-body-lg font-semibold text-foreground">{t('dashboard.currentRank')}</h3>
                <span className="text-xl sm:text-2xl">🏆</span>
              </div>
              <p className="text-lg sm:text-display-2 font-bold text-primary mb-1 sm:mb-2">-</p>
              <p className="text-small text-muted-foreground">{t('dashboard.currentRankDesc')}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default Dashboard
